import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Bath, 
  ChefHat, 
  Grid3X3, 
  Sparkles,
  Check,
  Wrench
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ServicesPage = () => {
  const services = [
    {
      icon: Bath,
      title: "Bathroom Tiling",
      description: "Complete bathroom transformations with waterproof, beautiful tiles.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/42w9spxp_Hexagon%20tiling%20bathroom.JPG",
      features: [
        "Fully waterproofed wet rooms",
        "Wall and floor tiling",
        "Shower enclosures",
        "Underfloor heating compatible",
        "Anti-slip floor options"
      ],
      priceRange: "From £800",
      link: "/quote",
      cta: "Check My Project"
    },
    {
      icon: ChefHat,
      title: "Kitchen Tiling",
      description: "Transform your kitchen with stunning backsplashes and floor tiles.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/7b3cl7yx_Splashback%20tiling.JPG",
      features: [
        "Splashback installations",
        "Floor tiling",
        "Island surrounds",
        "Heat-resistant materials",
        "Easy-clean finishes"
      ],
      priceRange: "From £400",
      link: "/quote",
      cta: "Check My Project"
    },
    {
      icon: Grid3X3,
      title: "Floor Tiling",
      description: "Durable, stunning floor tiles for any room in your home.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/hvgewxd3_Victorian%20floor%20tiles.JPG",
      features: [
        "Hallways and entrances",
        "Living areas",
        "Conservatories",
        "Utility rooms",
        "Underfloor heating"
      ],
      priceRange: "From £500",
      link: "/floor-tiling-quote",
      cta: "Get Floor Tiling Quote"
    },
    {
      icon: Sparkles,
      title: "Feature Walls & Splashbacks",
      description: "Make a statement with decorative feature walls.",
      image: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/mm3sw4gg_Feature%20Victorian%20tiling.JPG",
      features: [
        "Designer patterns",
        "Mosaic installations",
        "3D effect tiles",
        "Metallic finishes",
        "Custom designs"
      ],
      priceRange: "From £200",
      link: "/quote",
      cta: "Check My Project"
    }
  ];

  const bathroomRefitFeatures = [
    "Full bathroom renovation",
    "Shower installation",
    "Bath installation",
    "Toilet installation",
    "Basin installation",
    "Wall and floor tiling",
    "Bathroom layout updates",
    "Plumbing adjustments"
  ];

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
            Professional Tiling Services
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Expert installation for kitchens, bathrooms, floors, and feature walls across Greater Manchester and surrounding areas.
          </p>
          <Link to="/quote">
            <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="services-hero-cta">
              Get Your Free Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-section-${index}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                    <service.icon className="w-7 h-7" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4 font-montserrat">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-gray-700">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-green-600" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-6">
                    <span className="text-2xl font-bold text-primary">{service.priceRange}</span>
                    <Link to={service.link}>
                      <Button className="bg-primary hover:bg-primary/90" data-testid={`service-cta-${index}`}>
                        {service.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bathroom Refits Section */}
      <section className="section-padding bg-gray-50" data-testid="bathroom-refits-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/dnlvpa0u_Bathroom.JPG" 
                  alt="Bathroom Refit by DC Tiling"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                <Wrench className="w-7 h-7" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                Bathroom Refits
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Full bathroom renovation services including removal of existing suites, plumbing adjustments, tiling installation, fixture installation and finishing.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {bathroomRefitFeatures.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-600" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-6">
                <span className="text-2xl font-bold text-primary">From £3,500</span>
                <Link to="/bathroom-refit-quote">
                  <Button className="bg-primary hover:bg-primary/90" data-testid="bathroom-refit-cta">
                    Plan My Bathroom Refit
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Get an instant estimate and book your free quote inspection today.
          </p>
          <Link to="/quote">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="services-final-cta">
              Start My Tile Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
